import * as ISchema from "@schema";

// libraries
import * as React from "react";
import { connect } from "react-redux";
import { createFragmentContainer } from "react-relay";
import Helmet from "react-helmet";
import { Params } from "found";

// material-ui
import withStyles from "@material-ui/core/styles/withStyles";
import { Button, Paper, TextField } from "@material-ui/core";

// components
import { H2 } from "@components/ui";
import { GradientCard } from "@clockwise/web-commons/src/components/gradient-card";

// mutations
import { upsertNonUserDefragEmailResponse } from "@mutations";

// state
import { IReduxState } from "@reducers/root.reducer";

// util
import { track, TrackingEvents } from "@util/analytics.util";
import { windowLocation } from "@util/location.util";
import { extensionURLForBrowser } from "@clockwise/web-commons/src/util/browser.util";

// routing
import { withRouteQuery } from "@routing/routes.util";

// internal
import { IContainer, IProps, IState } from "./NonUserDefragSurvey.d";
import { nonUserDefragSurveyFragments, nonUserDefragSurveyQuery } from "./NonUserDefragSurvey.gql";
import { styles, survey } from "./NonUserDefragSurvey.styles";
import { TranslationContext } from "@clockwise/client-commons/src/providers/TranslationContext";

///////////////
// Component //
///////////////
class NonUserDefragSurveyCmpt extends React.Component<IProps, IState> {
  static contextType = TranslationContext;
  declare context: React.ContextType<typeof TranslationContext>;

  constructor(props: IProps) {
    super(props);

    this.state = {
      justSaved: false,
    };
  }

  private updateResponseType = (responseType: ISchema.NonUserDefragEmailResponseType) => () => {
    this.setState({
      responseType,
      justSaved: false,
    });
  };

  private updateAdditionalInformation = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    this.setState({
      additionalInformation: event.target.value,
      justSaved: false,
    });
  };

  private castResponseTypeQueryParam(param: string) {
    switch (param) {
      case "Yes":
        return ISchema.NonUserDefragEmailResponseType.Yes;
      case "No":
        return ISchema.NonUserDefragEmailResponseType.No;
      case "NotSure":
        return ISchema.NonUserDefragEmailResponseType.NotSure;
      default:
        return undefined;
    }
  }

  private goHome = () => {
    windowLocation.assign("NonUserDefragSurveyGoHome", "https://www.getclockwise.com/");
  };

  private save = (responseType: ISchema.NonUserDefragEmailResponseType) => {
    const { match } = this.props;

    upsertNonUserDefragEmailResponse(
      this.props.relay.environment,
      {
        responseType,
        emailInstanceId: match.params["emailInstanceId"],
        additionalInformation: this.state.additionalInformation,
      },
      () => this.setState({ responseType, justSaved: true }),
      this.goHome,
    );
  };

  public componentDidMount() {
    const { viewer, match } = this.props;
    const nonUserDefragEmailResponse = viewer && viewer.nonUserDefragEmailResponse;

    const responseType = this.castResponseTypeQueryParam(match.location.query["responseType"]);

    if (
      nonUserDefragEmailResponse &&
      nonUserDefragEmailResponse.__typename !== "GraphEntityError"
    ) {
      this.setState({
        responseType: nonUserDefragEmailResponse.responseType,
        additionalInformation: nonUserDefragEmailResponse.additionalInformation,
        justSaved: true,
      });
    } else if (responseType) {
      this.save(responseType);
    } else {
      this.goHome();
    }
  }

  public render() {
    const t = this.context;
    const { responseType } = this.state;
    const { classes } = this.props;

    const description = t("defrag-survey:description") as string;

    if (!responseType) {
      return null;
    }

    return (
      <div className={this.props.classes.container}>
        <Helmet>
          <title>{t("defrag-survey:title")}</title>
          <meta name="description" content={description} />
          <meta property="og:description" content={description} />
          <meta name="twitter:description" content={description} />
          <meta itemProp="description" content={description} />
        </Helmet>

        {/* HEADER */}
        <div className={this.props.classes.headerContainer}>
          <div className={this.props.classes.headerContent}>
            <h1 className={this.props.classes.h1}>Thank you for submitting your feedback!</h1>
            <h2 className={this.props.classes.h2}>
              We take your feedback very seriously, any additional information you can provide would
              be very appreciated.
            </h2>
          </div>
        </div>

        <div style={survey.container}>
          <Paper elevation={2} style={survey.paper}>
            <div style={survey.section}>
              <H2 style={survey.h2}>Were these changes helpful to your schedule?</H2>
              <div>
                <Button
                  onClick={this.updateResponseType(ISchema.NonUserDefragEmailResponseType.Yes)}
                  variant="outlined"
                  className={`${classes.button} ${
                    responseType === ISchema.NonUserDefragEmailResponseType.Yes ? "selected" : ""
                  }`}
                >
                  YES
                </Button>
                <Button
                  onClick={this.updateResponseType(ISchema.NonUserDefragEmailResponseType.No)}
                  variant="outlined"
                  className={`${classes.button} ${
                    responseType === ISchema.NonUserDefragEmailResponseType.No ? "selected" : ""
                  }`}
                >
                  NO
                </Button>
                <Button
                  onClick={this.updateResponseType(ISchema.NonUserDefragEmailResponseType.NotSure)}
                  variant="outlined"
                  className={`${classes.button} ${
                    responseType === ISchema.NonUserDefragEmailResponseType.NotSure
                      ? "selected"
                      : ""
                  }`}
                >
                  NOT SURE
                </Button>
              </div>
            </div>
            <div style={survey.section}>
              <H2 style={survey.h2}>Tell us more! Your input is valuable.</H2>
              <div>
                <TextField
                  style={{ maxWidth: 400 }}
                  value={this.state.additionalInformation}
                  onChange={this.updateAdditionalInformation}
                  fullWidth
                  multiline
                />
              </div>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                marginBottom: 30,
              }}
            >
              <Button
                style={survey.button}
                color="primary"
                variant="contained"
                onClick={() => this.save(this.state.responseType!)}
              >
                Submit
              </Button>
              {this.state.justSaved ? (
                <span style={survey.feedback}>Your feedback has been saved!</span>
              ) : null}
            </div>
          </Paper>
        </div>

        <div style={survey.gradientCardWrapper}>
          <GradientCard
            cardStyle={{ flex: 1, maxWidth: 650 }}
            label="Make time for what matters today."
            hideDismissButton={true}
            buttonLabel="Get Started For Free"
            onClick={() => track(TrackingEvents.NON_USER_DEFRAG_SURVEY.CLICKED_CTA)}
            href={`${extensionURLForBrowser}?utm_medium=referral&utm_source=product&utm_campaign=meeting-move-cta`}
          />
        </div>
      </div>
    );
  }
}

/////////////////
// With Styles //
/////////////////
export const NonUserDefragSurveyStyled = withStyles(styles)(NonUserDefragSurveyCmpt);

///////////
// Redux //
///////////
function mapStateToProps(_state: IReduxState, _ownProps: IContainer) {
  return {};
}

const NonUserDefragSurveyRedux = connect(mapStateToProps, {})(NonUserDefragSurveyStyled);

///////////
// Relay //
///////////
export const NonUserDefragSurvey = createFragmentContainer<IContainer>(
  NonUserDefragSurveyRedux,
  nonUserDefragSurveyFragments,
);

export const NonUserDefragSurveyPage = withRouteQuery(
  NonUserDefragSurvey as any,
  nonUserDefragSurveyQuery,
  { prepareVariables: (params: Params) => params },
);
